import React, { useMemo, useContext } from 'react';

import { useAtom } from 'jotai';

import CorrelationTable from './CorrelationTable';
import PredVsActualWithCI from './PredVsActualWithCI';
import TrainingDataChart from './TrainingDataChart';
import { kpiAtom, execDtAtom } from '../../../atoms';
import { FeatureAliasesContext } from '../../../context/MMMFeatureAliases';
import { unixToReadableDate } from '../helpers';
import { useMenu } from '../hooks/useMenu';
import { useModelFeatures } from '../hooks/useModelData';
import { ModelSectionHeader } from '../parts/stateless';

const ModelInfo = ({ rawData, trainingInfo }) => {
  const [kpi] = useAtom(kpiAtom);
  const [execDt] = useAtom(execDtAtom);
  const isConversionKPI = kpi === 'conversions';
  const modelDate = unixToReadableDate(execDt);

  const { metrics: metricsRaw, correlation_matrix: correlationData, predicted_vs_actual_test: predVsActualTestData, predicted_vs_actual_training: predVsActual } = rawData;
  const metrics = metricsRaw?.slice(-1)[0];

  const { trainingData } = trainingInfo;

  const { features } = useModelFeatures(rawData);
  const { featureAliasesMap: featureNameMap } = useContext(FeatureAliasesContext);

  const impKeys = useMemo(() => {
    if (!trainingData || !trainingData.length) return [];
    return Object.keys(trainingData[0]).filter(key => key.endsWith('_IMP'));
  }, [trainingData]);

  const trainingChartData = useMemo(() => {
    if (!trainingData || !trainingData.length) return [];
    return trainingData.map(accu => {
      const ret = {};
      Object.keys(accu).forEach(k => {
        if (!impKeys.includes(k)) {
          ret[k] = accu[k];
        }
      });
      return ret;
    });
  }, [trainingData, impKeys]);

  const items = ['Fit', 'Features'];
  const { Component: ResultsMenu, selectedItem } = useMenu(items, items[0], false, false, false);

  const trainingMax = predVsActual ? predVsActual.reduce((accu, c) => {
    Object.keys(c).forEach(k => {
      if (k === 'date') return accu;
      /* eslint-disable-next-line no-param-reassign */
      accu = Math.max(accu, c[k]);
    });
    return accu;
  }, 0) : 0;

  const testMax = predVsActualTestData ? predVsActualTestData.reduce((accu, c) => {
    Object.keys(c).forEach(k => {
      if (k === 'date') return accu;
      /* eslint-disable-next-line no-param-reassign */
      accu = Math.max(accu, c[k]);
    });
    return accu;
  }, 0) : 0;

  const yMax = (Math.ceil(Math.max(trainingMax, testMax) / 100) * 100) + 100;

  return (
    <>
      <ResultsMenu />
      {selectedItem === 'Fit' && (
        <div>
          <ModelSectionHeader text="Model Results" right={!!modelDate && `Build Date: ${modelDate}`} />
          <div style={{ paddingLeft: 10, paddingRight: 10 }}>
            <PredVsActualWithCI data={predVsActual} periodName="Training" isConversionKPI={isConversionKPI} metrics={metrics} yMax={yMax} />
            <PredVsActualWithCI data={predVsActualTestData} periodName="Test" isConversionKPI={isConversionKPI} yMax={yMax} />
          </div>
        </div>
      )}
      {selectedItem === 'Features' && (
        <>
          <TrainingDataChart {...{ isConversionKPI, trainingData: trainingChartData, features, featureNameMap, predVsActual }} />
          <CorrelationTable {...{ isConversionKPI, correlationData, features }} />
        </>
      )}
    </>
  );
};

export default ModelInfo;
